import React, { useState, memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { Popover, Chip, Text, Advice, Spacer } from '@Sage/hoi-poi-ui';

import './styles.scss';

const PopoverMenu = memo(({ title, advice, options, onChange, value, children }) => {
    const [visible, setVisible] = useState(false);
    const contentRef = useRef(null);

    const onClickOutside = useCallback((e) => {
        if (!contentRef.current?.contains(e.target)) {
            setVisible(false);
        }
    }, []);

    useEffect(() => {
        document.addEventListener('mousedown', onClickOutside);
        return () => {
            document.removeEventListener('mousedown', onClickOutside);
        };
    }, [onClickOutside]);

    const handleChange = useCallback(
        (value) => {
            onChange?.(value);
            setVisible(false);
        },
        [onChange],
    );

    const togglePopover = useCallback(() => {
        setVisible((current) => !current);
    }, []);

    const popoverProps = useMemo(
        () => ({
            className: 'fm-popover-menu__popover',
            placement: 'bottomLeft',
            content: (
                <div
                    ref={(cRef) => {
                        contentRef.current = cRef;
                    }}
                >
                    <div className="fm-popover-menu__content">
                        <Text type="subtitle1">{title}</Text>
                        {advice && (
                            <>
                                <Spacer y={2} />
                                <Advice type="semanticInfo" showIcon showCollapse={false}>
                                    {advice}
                                </Advice>
                            </>
                        )}
                        <Spacer y={2} />
                        <div className="fm-popover-menu__options">
                            {options.map((option) => (
                                <div
                                    key={option.value}
                                    className={`fm-popover-menu__option ${
                                        value?.value === option.value
                                            ? 'fm-popover-menu__option--selected'
                                            : ''
                                    }`}
                                    onClick={() => handleChange(option.value)}
                                >
                                    <Text>{option.label}</Text>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            ),
            overrides: {
                root: {
                    visible,
                },
            },
        }),
        [advice, handleChange, options, title, value?.value, visible],
    );

    return (
        <div className="fm-popover-menu">
            <Popover {...popoverProps}>
                <Chip size="large" isUnfolded isActive={visible} onClick={togglePopover}>
                    {children}
                    {!children && <Text>{value?.label}</Text>}
                </Chip>
            </Popover>
        </div>
    );
});

PopoverMenu.displayName = 'PopoverMenu';

export default PopoverMenu;
